/**
 * Password validation utilities
 */

/**
 * Validates a password according to the application requirements
 * @param {string} password - The password to validate
 * @returns {Object} - Validation result with isValid boolean and error messages
 */
export const validatePassword = password => {
  const errors = []

  // Check for Greek characters (not allowed)
  const hasGreekChars = /[\u0370-\u03FF\u1F00-\u1FFF]/.test(password)
  if (hasGreekChars) {
    errors.push("Ο κωδικός δεν μπορεί να περιέχει ελληνικούς χαρακτήρες")
  }

  // Check minimum length
  if (!password || password.length < 4) {
    errors.push("Ο κωδικός πρέπει να έχει τουλάχιστον 4 χαρακτήρες")
  }

  // Check for at least one letter or symbol
  const hasLetterOrSymbol = /[a-zA-Z!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
  if (!hasLetterOrSymbol) {
    errors.push("Ο κωδικός πρέπει να περιέχει τουλάχιστον ένα γράμμα ή σύμβολο")
  }

  // Check for at least one number
  const hasNumber = /\d/.test(password)
  if (!hasNumber) {
    errors.push("Ο κωδικός πρέπει να περιέχει τουλάχιστον έναν αριθμό")
  }

  return {
    isValid: errors.length === 0,
    errors: errors
  }
}

/**
 * Validates password and returns a simple boolean (for quick checks)
 * @param {string} password - The password to validate
 * @returns {boolean} - True if password is valid
 */
export const isPasswordValid = password => {
  const result = validatePassword(password)
  return result.isValid
}

/**
 * Gets password strength level
 * @param {string} password - The password to check
 * @returns {string} - Strength level: 'weak', 'medium', 'strong'
 */
export const getPasswordStrength = password => {
  if (!password || password.length < 4) {
    return "weak"
  }

  let score = 0

  // Length bonus
  if (password.length >= 8) score += 1
  if (password.length >= 12) score += 1

  // Character variety bonus
  if (/[a-z]/.test(password)) score += 1
  if (/[A-Z]/.test(password)) score += 1
  if (/\d/.test(password)) score += 1
  if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) score += 1

  if (score < 3) return "weak"
  if (score < 5) return "medium"
  return "strong"
}

// Email validation function
export const validateEmail = email => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!email.trim()) {
    return { isValid: false, error: "Το email είναι υποχρεωτικό" }
  }
  if (!emailRegex.test(email)) {
    return { isValid: false, error: "Παρακαλώ εισάγετε έγκυρο email" }
  }
  return { isValid: true, error: "" }
}
